import { Category } from '@shared/types/product/Category';
import { resolveLocalizedUrl } from '@/utils/resolve-localized-url';
import { collectImmediateChildCategories } from './categoryFilter';
import type { Locale } from '@/project.config';

export interface SubcategoryLink {
  label: string;
  href: string;
}

interface BuildSubcategoryNavArgs {
  /** The category being viewed. Absent on /search and Collection pages. */
  category: Pick<Category, 'categoryId'> | undefined;
  /** The flat category list already held client-side. */
  allCategories: Category[];
  /**
   * `categories.name` → product count, from the UNREFINED sibling query
   * (see `includeUnrefinedCategoryFacet`). Must not be the main query's facet:
   * the block has to list the same children whatever the grid is filtered to.
   */
  categoryFacetCounts: Record<string, number> | undefined;
  locale: Locale;
}

/**
 * EWM-832 — the child categories to list beneath a category heading.
 *
 * Returns `undefined` (not an empty array) when there is nothing to show, so
 * the page can omit the block entirely rather than render an empty container
 * that would leave a gap.
 *
 * Ordering: the incoming array order is preserved. `queryCategories` sorts by
 * the commercetools `orderHint`, so siblings already arrive in the order
 * authored in CT. NOTE: the AC asks for "explicit commercetools order if set,
 * falling back to alphabetical where no explicit order is set" — that fallback
 * is not implementable today and is arguably unreachable: `orderHint` is not
 * exposed to the frontend at all (it is absent from the shared `Category` type
 * and from `commercetoolsCategoryToCategory`), and commercetools assigns every
 * category an orderHint, so "no explicit order" cannot be detected client-side.
 * Surfacing it would need a BFF change. Preserving CT's own order satisfies the
 * intent; see the PR description.
 */
export const buildSubcategoryNav = ({
  category,
  allCategories,
  categoryFacetCounts,
  locale,
}: BuildSubcategoryNavArgs): SubcategoryLink[] | undefined => {
  if (!category) return undefined;

  const links: SubcategoryLink[] = [];
  for (const child of collectImmediateChildCategories(category, allCategories)) {
    const label = child.name?.trim();
    if (!label) continue;

    // "Only child categories that resolve to at least one product are listed."
    // A child absent from the facet contributed no products to the unrefined
    // result set, so clicking it would land the customer on an empty listing.
    if ((categoryFacetCounts?.[label] ?? 0) <= 0) continue;

    // Category slugs are authored en-AU only, so a bare `_url[locale]` is empty
    // on NZ — resolveLocalizedUrl carries the EWM-854 fallback chain.
    const href = resolveLocalizedUrl(child._url, locale);
    if (!href) continue;

    links.push({ label, href });
  }

  return links.length > 0 ? links : undefined;
};
